import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Button } from "@shared/components/ui/button";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { Badge } from "@shared/components/ui/badge";
import { Package } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { STATUS_LIST, STATUS_OPTIONS } from "@shared/utils/constants";
import * as packagesService from "../services/packagesService";

const LIVREUR_STATUS_OPTIONS = [
  {
    value: STATUS_LIST[2], // "to_pick_up"
    label: "To Pick Up",
    description: "Package ready for pickup",
    color: "bg-purple-500",
  },
  {
    value: STATUS_LIST[3], // "picked_up"
    label: "Picked Up",
    description: "Package picked up from warehouse",
    color: "bg-blue-500",
  },
  {
    value: STATUS_LIST[4], // "in_transit"
    label: "In Transit",
    description: "Package is in transit",
    color: "bg-yellow-500",
  },
  {
    value: STATUS_LIST[5], // "arrived_at_warehouse"
    label: "Arrived at Warehouse",
    description: "Package arrived at delivery warehouse",
    color: "bg-cyan-500",
  },
  {
    value: STATUS_LIST[6], // "warehouse_processing"
    label: "Processing at Warehouse",
    description: "Package being processed at warehouse",
    color: "bg-teal-500",
  },
  {
    value: STATUS_LIST[7], // "to_deliver"
    label: "To Deliver",
    description: "Package ready for delivery",
    color: "bg-lime-500",
  },
  {
    value: STATUS_LIST[8], // "self_pickup_putaway"
    label: "Self-pickup Putaway",
    description: "Package set aside for self-pickup",
    color: "bg-emerald-500",
  },
  {
    value: STATUS_LIST[9], // "delivering"
    label: "Delivering",
    description: "Package is out for delivery",
    color: "bg-orange-500",
  },
  {
    value: STATUS_LIST[10], // "signed"
    label: "Signed",
    description: "Package successfully delivered and signed",
    color: "bg-green-500",
  },
  {
    value: "abnormal_signed",
    label: "Abnormal Signed",
    description: "Package delivered with issues",
    color: "bg-amber-500",
  },
  {
    value: "return_collection",
    label: "Return Collection",
    description: "Package returned to sender",
    color: "bg-red-500",
  },
];

export function LivreurStatusUpdateDialog({ packageData, trigger }) {
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [note, setNote] = useState("");
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Update status mutation for livreur
  const updateStatusMutation = useMutation({
    mutationFn: ({ status, note }) =>
      packagesService.updatePackageStatusByLivreur(packageData._id, {
        status,
        note,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", packageData._id] });
      queryClient.invalidateQueries({ queryKey: ["livreur-packages"] });
      toast.success(t("Package status updated successfully"));
      setOpen(false);
      setSelectedStatus("");
      setNote("");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("Failed to update package status")
      );
    },
  });

  const handleUpdateStatus = () => {
    if (!selectedStatus) {
      toast.error(t("Please select a status"));
      return;
    }
    updateStatusMutation.mutate({ status: selectedStatus, note });
  };

  const getStatusOption = (value) =>
    LIVREUR_STATUS_OPTIONS.find((option) => option.value === value);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t("Update Package Status")}
          </DialogTitle>
          <DialogDescription>
            {t("Update the delivery status of this package")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Status */}
          <div className="p-4 rounded-lg border bg-muted/50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {t("Current Status")}:
              </span>
              <Badge variant="outline">
                {getStatusOption(packageData.status)?.label ||
                  packageData.status}
              </Badge>
            </div>

            <div className="mt-2 space-y-1">
              <p className="text-sm">
                <span className="font-medium">{t("Customer")}:</span>{" "}
                {packageData.customer.name}
              </p>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{t("Phone")}:</span>{" "}
                {packageData.customer.phone}
              </p>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{t("Address")}:</span>{" "}
                {packageData.customer.address}, {packageData.customer.city}
                {packageData.customer.secteur && ` - ${packageData.customer.secteur}`}
              </p>
            </div>
          </div>

          {/* Status Selection */}
          <div className="space-y-2">
            <Label htmlFor="status">{t("New Status")}</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t("Select new status")} />
              </SelectTrigger>
              <SelectContent>
                {LIVREUR_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${option.color}`} />
                      <div className="flex flex-col">
                        <span>{t(option.label)}</span>
                        <span className="text-xs text-muted-foreground">
                          {t(option.description)}
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">
              {t("Note")} ({t("Optional")})
            </Label>
            <Textarea
              id="note"
              placeholder={t("Add a note about this status update...")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>

          {/* Status Description */}
          {selectedStatus && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className={`w-3 h-3 rounded-full ${
                    getStatusOption(selectedStatus)?.color
                  }`}
                />
                <span className="text-sm font-medium">
                  {t(getStatusOption(selectedStatus)?.label)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {t(getStatusOption(selectedStatus)?.description)}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleUpdateStatus}
            disabled={updateStatusMutation.isPending || !selectedStatus}
          >
            {updateStatusMutation.isPending
              ? t("Updating...")
              : t("Update Status")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LivreurStatusUpdateDialog;
